import { config } from '@guest-widgets/shared/src/config';
import { shallowEqual } from '@guest-widgets/shared/src/utils/shallowEqual';
import mergeWith from 'lodash.mergewith';
import dayjs from 'dayjs';

import { Filters, Sort } from '../filters';
import type { FeatureToggleFilters } from '../useFeatureToggleFilters';

export type SearchParam = [key: string, value: string | undefined];

export enum FilterSearchParam {
  DateFrom = 'date-from',
  DateTo = 'date-to',
  LocationName = 'location',
  Latitude = 'latitude',
  Longitude = 'longitude',
  Radius = 'radius',
  PriceFrom = 'price-from',
  PriceTo = 'price-to',
  Categories = 'categories',
  Misc = 'misc',
  Languages = 'languages',
  Sort = 'sort',
}

export const mapToSearchParams = (filters: Filters, defaultFilters: Filters): SearchParam[] => {
  /**
   * Returns undefined if the selected part of the filters is equal to the default one,
   * the stringified version otherwise
   */
  const nonDefault = (
    select: (filter: Filters) => any,
    stringify?: (filterPart: any) => string
  ): string | undefined => {
    const filter = select(filters);
    const defaultFilter = select(defaultFilters);

    if (shallowEqual(filter, defaultFilter)) return undefined;
    if (typeof filter === 'undefined') return '';
    if (!stringify) {
      if (filter.toString) return filter.toString();
      return filter;
    }
    return stringify(filter);
  };

  const params: Record<FilterSearchParam, string | undefined> = {
    [FilterSearchParam.DateFrom]: nonDefault((filters) => filters.dates?.[0], stringifyDate),
    [FilterSearchParam.DateTo]: nonDefault((filters) => filters.dates?.[1], stringifyDate),
    [FilterSearchParam.LocationName]: nonDefault((filters) => filters.location?.name),
    [FilterSearchParam.Latitude]: nonDefault((filters) => filters.location?.latitude),
    [FilterSearchParam.Longitude]: nonDefault((filters) => filters.location?.longitude),
    [FilterSearchParam.Radius]: nonDefault((filters) => filters.location?.radius),
    [FilterSearchParam.PriceFrom]: nonDefault((filters) => filters.price?.[0]),
    [FilterSearchParam.PriceTo]: nonDefault((filters) => filters.price?.[1]),
    [FilterSearchParam.Categories]: nonDefault((filters) => filters.categories, stringifyList),
    [FilterSearchParam.Misc]: nonDefault((filters) => filters.misc, stringifyList),
    [FilterSearchParam.Languages]: nonDefault((filters) => filters.languages, stringifyList),
    [FilterSearchParam.Sort]: nonDefault((filters) => filters.sort, stringifySort),
  };

  return Object.entries(params).map(([key, value]) => [prefix(key), value]);
};

export const mapToFilters = (
  defaultFilters: Filters,
  searchParams: Pick<URLSearchParams, 'get'>,
  featureToggleFilters: FeatureToggleFilters
): Filters => {
  const filters = { ...defaultFilters };

  if (featureToggleFilters.dates) {
    const dateFromString = searchParams.get(prefix(FilterSearchParam.DateFrom));
    const dateToString = searchParams.get(prefix(FilterSearchParam.DateTo));
    const dateFrom = dateFromString ? dayjs(dateFromString).toDate() : undefined;
    const dateTo = dateToString ? dayjs(dateToString).toDate() : undefined;

    if (dateFromString === '') {
      delete filters.dates;
    } else if (dateFrom !== null && dateFrom !== undefined) {
      filters.dates = [dateFrom, dateTo || new Date(dateFrom)];
    }
  }

  if (featureToggleFilters.location) {
    const locationName = searchParams.get(prefix(FilterSearchParam.LocationName));
    const latitude = searchParams.get(prefix(FilterSearchParam.Latitude));
    const longitude = searchParams.get(prefix(FilterSearchParam.Longitude));
    const radiusString = searchParams.get(prefix(FilterSearchParam.Radius));

    const radius = parseInt(radiusString || '');
    if (locationName === '' && latitude === '' && longitude === '' && radiusString === '') {
      delete filters.location;
    } else {
      const newLocation = mergeWith(
        {},
        filters.location,
        {
          name: locationName,
          latitude,
          longitude,
          radius,
        },
        (objValue, srcValue) => (srcValue || Number(srcValue) ? srcValue : objValue)
      );

      if (
        newLocation.name &&
        newLocation.latitude &&
        newLocation.longitude &&
        newLocation.radius > 0
      ) {
        filters.location = newLocation;
      }
    }
  }

  if (featureToggleFilters.price) {
    const priceFromString = searchParams.get(prefix(FilterSearchParam.PriceFrom));
    const priceFrom = parseInt(priceFromString || '');
    const priceToString = searchParams.get(prefix(FilterSearchParam.PriceTo));
    const priceTo = parseInt(priceToString || '');
    if (priceFromString === '' && priceToString === '') {
      delete filters.price;
    } else if (!isNaN(priceFrom) && !isNaN(priceTo)) {
      filters.price = [priceFrom, priceTo];
    }
  }

  if (featureToggleFilters.categories) {
    const categories = searchParams.get(prefix(FilterSearchParam.Categories));
    if (categories === '') {
      delete filters.categories;
    } else if (categories) {
      filters.categories = parseList(categories);
    }
  }

  if (featureToggleFilters.misc) {
    const misc = searchParams.get(prefix(FilterSearchParam.Misc));
    if (misc === '') {
      delete filters.misc;
    } else if (misc) {
      filters.misc = parseList(misc);
    }
  }

  if (featureToggleFilters.languages) {
    const languages = searchParams.get(prefix(FilterSearchParam.Languages));
    if (languages === '') {
      delete filters.languages;
    } else if (languages) {
      filters.languages = parseList(languages);
    }
  }

  if (featureToggleFilters.sort) {
    const sortString = searchParams.get(prefix(FilterSearchParam.Sort));
    const sort = parseSort(sortString || '');
    if (sortString === '') {
      delete filters.sort;
    } else if (sort) {
      filters.sort = sort;
    }
  }

  return filters;
};

const stringifyDate = (date: Date) =>
  `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;

const stringifySort = (sort: Sort): string => sort;
const parseSort = (sortString: string): Sort | undefined =>
  ({
    [Sort.Distance]: Sort.Distance,
    [Sort.HighestPrice]: Sort.HighestPrice,
    [Sort.LowestPrice]: Sort.LowestPrice,
  }[sortString]);

const stringifyList = (list: string[]) => list.join(',');
const parseList = (listString: string) => listString.split(',');

const prefix = (key: string) => `${config.filterQueryPrefix}${key}`;
